import Paragraph from '@tiptap/extension-paragraph';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Strike from '@tiptap/extension-strike';
import Italic from '@tiptap/extension-italic';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import HardBreak from '@tiptap/extension-hard-break';
import Code from '@tiptap/extension-code';
import Bold from '@tiptap/extension-bold';
import Blockquote from '@tiptap/extension-blockquote';
import Link from '@tiptap/extension-link';
import CodeBlock from '@tiptap/extension-code-block';
import {
  MarkdownSerializer as ProseMirrorMarkdownSerializer,
  defaultMarkdownSerializer,
  MarkdownSerializerState,
} from '@tiptap/pm/markdown';
import { marked } from 'marked';
import {
  DOMParser as ProseMirrorDOMParser,
  Schema,
  Node as PNode,
  Mark,
} from 'prosemirror-model';
import { JSONContent } from '@tiptap/react';

marked.setOptions({
  breaks: true,
});

export const renderOrderedList = (
  state: MarkdownSerializerState,
  node: PNode
) => {
  const { parens } = node.attrs;
  const start = node.attrs['start'] || 1;
  const maxW = String(start + node.childCount - 1).length;
  const space = state.repeat(' ', maxW + 2);
  const delimiter = parens ? ')' : '.';
  state.renderList(node, space, (i: number) => {
    const nStr = String(start + i);
    return `${state.repeat(' ', maxW - nStr.length) + nStr}${delimiter} `;
  });
};

export const isPlainURL = (
  link: Mark,
  parent: PNode,
  index: number,
  side: number
) => {
  if (link.attrs['title'] || !/^\w+:/.test(link.attrs['href'])) return false;
  const content = parent.child(index + (side < 0 ? -1 : 0));
  if (
    !content.isText ||
    content.text !== link.attrs['href'] ||
    content.marks[content.marks.length - 1] !== link
  )
    return false;
  if (index === (side < 0 ? 1 : parent.childCount - 1)) return true;
  const next = parent.child(index + (side < 0 ? -2 : 1));
  return !link.isInSet(next.marks);
};

export const serializerMarks = {
  ...defaultMarkdownSerializer.marks,
  [Bold.name]: defaultMarkdownSerializer.marks['strong'],
  [Strike.name]: {
    open: '~~',
    close: '~~',
    mixable: true,
    expelEnclosingWhitespace: true,
  },
  [Italic.name]: {
    open: '_',
    close: '_',
    mixable: true,
    expelEnclosingWhitespace: true,
  },
  [Code.name]: defaultMarkdownSerializer.marks['code'],
  [Link.name]: {
    open(
      state: MarkdownSerializerState,
      mark: Mark,
      parent: PNode,
      index: number
    ) {
      return isPlainURL(mark, parent, index, 1) ? '<' : '[';
    },
    close(
      state: MarkdownSerializerState,
      mark: Mark,
      parent: PNode,
      index: number
    ) {
      const href = mark.attrs['canonicalSrc'] || mark.attrs['href'];

      return isPlainURL(mark, parent, index, -1)
        ? '>'
        : `](${state.esc(href)}${
            mark.attrs['title'] ? ` ${mark.attrs['title']}` : ''
          })`;
    },
  },
};

export const serializerNodes = {
  ...defaultMarkdownSerializer.nodes,
  [Paragraph.name]: defaultMarkdownSerializer.nodes['paragraph'],
  [BulletList.name]: defaultMarkdownSerializer.nodes['bullet_list'],
  [ListItem.name]: defaultMarkdownSerializer.nodes['list_item'],
  [HorizontalRule.name]: defaultMarkdownSerializer.nodes['horizontal_rule'],
  [OrderedList.name]: renderOrderedList,
  [HardBreak.name]: (state: MarkdownSerializerState) => state.write('\n'),
  [CodeBlock.name]: (state: MarkdownSerializerState, node: PNode) => {
    state.write(`\`\`\`${node.attrs['language'] || ''}\n`);
    state.text(node.textContent, false);
    state.ensureNewLine();
    state.write('```');
    state.closeBlock(node);
  },
  [Blockquote.name]: (state: MarkdownSerializerState, node: PNode) => {
    if (node.attrs['multiline']) {
      state.write('>>>');
      state.ensureNewLine();
      state.renderContent(node);
      state.ensureNewLine();
      state.write('>>>');
      state.closeBlock(node);
    } else {
      state.wrapBlock('> ', null, node, () => state.renderContent(node));
    }
  },
};

export const serialize = (schema: Schema, content: JSONContent) => {
  const proseMirrorDocument = schema.nodeFromJSON(content);
  const serializer = new ProseMirrorMarkdownSerializer(
    serializerNodes,
    serializerMarks
  );
  return serializer.serialize(proseMirrorDocument, {
    tightLists: true,
  });
};

export const deserialize = (schema: Schema, content: string) => {
  const cleanedContent = content.replace(/\\'/g, "'").replace(/\\\\/g, '\\');
  const html = marked.parse(cleanedContent);
  if (!html) return null;
  const parser = new DOMParser();
  const { body } = parser.parseFromString(html, 'text/html');
  body.append(document.createComment(cleanedContent));
  const state = ProseMirrorDOMParser.fromSchema(schema).parse(body);
  return state.toJSON();
};
